import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDialog } from '../context/DialogContext';
import useIsMobile from '../hooks/useIsMobile';
import { SurfacePage, SoftSurface } from '../components/surface';
import {
  ClubPanel, ClubBackPill, PillButton, TagPill, useArmed, useToast, Toast, Face,
  MUTED, SUBTLE, CHIP, INSET, RED, GREEN, CYAN, MAGENTA, YELLOW, RULE, panelTitle,
} from '../components/club/clubUi';
import { LoadingSpinner, ErrorState } from '../components';
import clubService from '../services/clubService';
import clubMembershipService from '../services/clubMembershipService';
import { money, monthYearLabel, dateYearLabel, plural } from '../utils/surfaceFormat';

/**
 * MEMBERSHIPS & REVENUE — design_handoff_club_pages/README.md §5 (`/clubs/:id/revenue`; `/owner`
 * redirects here). Black header card → 4-up stats → MEMBERS · N with filters, EXPORT CSV and
 * armed REFUND / COMP → rail with THIS MONTH and LAST 6 MONTHS. CHANGE THE PRICE sits in the top
 * bar and goes to CLUB SETTINGS › MEMBERSHIP.
 *
 * Money is rendered as inventory/03 §3.6 describes it: fees are charged to members ON TOP of
 * the price, the host is credited the full sticker price. YOURS is the sum of paid invoice
 * amounts. There is no trial status.
 *
 * Authorization is server-side (AssertOwnerOrAdminAsync). We bounce on the role in the club
 * DTO and, as before, on the "Owner or admin" error string.
 */

const FILTERS = [
  ['', 'ALL'], ['active', 'ACTIVE'], ['past_due', 'PAST DUE'], ['canceled', 'CANCELLED'],
  ['expired', 'LAPSED'], ['comp', 'COMP'],
];

/** paid_status → pill. No `trial` exists on the model, so none is rendered. */
export function statusPill(paidStatus) {
  switch (paidStatus) {
    case 'active': return { label: 'PAID', tone: 'black' };
    case 'comp': return { label: 'COMP', tone: 'outline' };
    case 'free': return { label: 'FREE', tone: 'ghost' };
    case 'past_due': return { label: 'PAST DUE', tone: 'yellow' };
    case 'expired': return { label: 'LAPSED', tone: 'redOutline' };
    case 'canceled': return { label: 'CANCELLED', tone: 'hairline' };
    default: return { label: String(paidStatus || '—').toUpperCase(), tone: 'ghost' };
  }
}

/** README §Pills (revenue): PAID black · COMP white + black ring · PAST DUE yellow · LAPSED white + red ring/text · CANCELLED hairline/grey. */
const PILL_LOOK = {
  black: { bg: '#000', fg: '#fff' },
  outline: { bg: '#fff', fg: '#000', ring: '#000' },
  ghost: { bg: CHIP, fg: '#000' },
  yellow: { bg: YELLOW, fg: '#000' },
  redOutline: { bg: '#fff', fg: RED, ring: RED },
  hairline: { bg: '#E5E7EB', fg: MUTED },
};

/** `$12.00 / MONTH` · `$40.00 ONCE` · `FREE` from the club's billing config. */
export function priceLabel(club) {
  if (!club || !club.membershipMode || club.membershipMode === 'free') return 'FREE';
  const p = money(club.membershipPriceCents || 0);
  if (club.membershipMode === 'one_time') return `${p} ONCE`;
  return `${p} / ${club.membershipBillingPeriod === 'annual' ? 'YEAR' : 'MONTH'}`;
}

function throughLabel(m) {
  if (m.paidThroughAt) return `THROUGH ${dateYearLabel(m.paidThroughAt)}`;
  if (m.paidStatus === 'active' || m.paidStatus === 'comp') return 'LIFETIME';
  return null;
}

/** Roster → CSV. Quotes every cell; the browser download is the only side effect. */
export function membersToCsv(members, currency) {
  const q = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const head = ['Member', 'Handle', 'Status', 'Joined', 'Paid through', 'Paid', 'Currency', 'Renews'];
  const rows = members.map((m) => [
    m.displayName, m.handle ? `@${m.handle}` : '', m.paidStatus, m.joinedAt ? new Date(m.joinedAt).toISOString().slice(0, 10) : '',
    m.paidThroughAt ? new Date(m.paidThroughAt).toISOString().slice(0, 10) : (m.paidStatus === 'active' ? 'lifetime' : ''),
    m.paidPriceCents != null ? (m.paidPriceCents / 100).toFixed(2) : '', m.paidCurrency || currency || '', m.hasActiveSubscription ? 'yes' : 'no',
  ]);
  return [head, ...rows].map((r) => r.map(q).join(',')).join('\n');
}

/** LAST 6 MONTHS: black bars, the current month magenta, value labels above. */
function Bars({ months }) {
  const max = Math.max(0, ...months.map((m) => m.amountCents || 0));
  return (
    <div className="flex items-end" style={{ gap: 8, height: 140 }} data-testid="revenue-bars">
      {months.map((m, i) => {
        const h = max > 0 ? Math.round(((m.amountCents || 0) / max) * 100) : 0;
        const current = i === months.length - 1;
        return (
          <span key={m.month} className="flex h-full flex-1 flex-col items-center justify-end" style={{ gap: 6 }} title={`${m.label || m.month} · ${money(m.amountCents || 0)}`}>
            <span style={{ fontSize: 9, fontWeight: 700, color: MUTED }}>{m.amountCents ? `$${Math.round(m.amountCents / 100)}` : ''}</span>
            <span className="w-full" style={{ height: `${h}%`, minHeight: 2, borderRadius: '8px 8px 3px 3px', background: current ? MAGENTA : '#000' }} data-testid="revenue-bar" data-height={h} data-current={current ? '1' : undefined} />
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.08em', color: MUTED }}>{m.label || m.month}</span>
          </span>
        );
      })}
    </div>
  );
}

export default function ClubOwnerDashboardPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showAlert } = useDialog();
  const phone = useIsMobile();
  const { toast, say } = useToast();
  const { isArmed, confirm, disarm } = useArmed();

  const [club, setClub] = useState(null);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState(null);
  const [members, setMembers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [membersLoading, setMembersLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const [clubR, statsR, historyR] = await Promise.all([
      clubService.getClub(id),
      clubMembershipService.getOwnerStats(id),
      clubMembershipService.getRevenueHistory(id, 6),
    ]);
    if (statsR.error?.includes('Owner or admin') || (clubR.success && clubR.data && clubR.data.userRole !== 'owner' && clubR.data.userRole !== 'admin')) {
      navigate(`/clubs/${id}`, { replace: true });
      return;
    }
    if (!clubR.success || !clubR.data) { setError(clubR.error || 'Club not found'); setLoading(false); return; }
    setClub(clubR.data);
    if (statsR.success) setStats(statsR.data); else setError(statsR.error || 'Could not load revenue');
    // The history endpoint is newer than the stats one — without it the rail falls back to the flat stats.
    setHistory(historyR.success && historyR.data ? historyR.data : null);
    setLoading(false);
  }, [id, navigate]);

  const loadMembers = useCallback(async () => {
    setMembersLoading(true);
    const r = await clubMembershipService.listMembersForOwner(id, { status: statusFilter || null });
    if (r.success) setMembers(r.data || []);
    setMembersLoading(false);
  }, [id, statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadMembers(); }, [loadMembers]);

  const refresh = async () => { await Promise.all([load(), loadMembers()]); };

  // Two-step: REFUND → `REFUND $12.00?` → the refund; COMP → `COMP · FREE FOR LIFE?` → the comp.
  const handleRefund = async (m) => {
    if (!confirm(`refund:${m.userId}`)) return;
    const r = await clubMembershipService.refundMember(id, m.userId);
    if (r.success) { say(`REFUNDED ${(m.displayName || '').split(' ')[0].toUpperCase()} · ACCESS ENDS NOW`); await refresh(); }
    else showAlert({ title: 'REFUND FAILED', message: r.error || '', type: 'error' });
  };

  const handleComp = async (m) => {
    if (!confirm(`comp:${m.userId}`)) return;
    const r = await clubMembershipService.compMembership(id, m.userId);
    if (r.success) { say(`${(m.displayName || '').split(' ')[0].toUpperCase()} IS COMPED`); await refresh(); }
    else showAlert({ title: 'COMP FAILED', message: r.error || '', type: 'error' });
  };

  const exportCsv = () => {
    const csv = membersToCsv(members, club?.currency);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const file = `${(club?.name || 'club').replace(/[^\w-]+/g, '-').toLowerCase()}-members${statusFilter ? `-${statusFilter}` : ''}.csv`;
    a.href = url;
    a.download = file;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    say(`EXPORTED ${members.length} ROWS · ${file.toUpperCase()}`);
  };

  const goPrice = () => navigate(`/clubs/${id}/manage?tab=membership`);

  const frame = (children) => (
    <SoftSurface>
      <SurfacePage bare showTopBarOnMobile>
        <main className="mx-auto flex w-full flex-col font-mono" style={{ maxWidth: 1280, padding: phone ? '12px 12px 24px' : '24px 32px 72px', gap: phone ? 10 : 16, boxSizing: 'border-box' }} data-testid="club-revenue">
          <div className="flex flex-wrap items-center justify-between" style={{ gap: 8 }}>
            <ClubBackPill to={`/clubs/${id}`}>{club?.name || 'CLUB'}</ClubBackPill>
            {club && <PillButton size={40} fontSize={11} onClick={goPrice} style={{ padding: '0 18px' }} data-testid="change-price">CHANGE THE PRICE</PillButton>}
          </div>
          {children}
        </main>
        <Toast text={toast} phone={phone} />
      </SurfacePage>
    </SoftSurface>
  );

  if (loading) return frame(<div className="py-24 text-center"><LoadingSpinner label="LOADING..." /></div>);
  if (error || !club || !stats) return frame(<div className="py-16"><ErrorState message={error} onRetry={load} /></div>);

  const paying = stats.activeMembersCount ?? 0;
  const lapsed = stats.lapsedCount ?? ((stats.pastDueCount ?? 0) + (stats.expiredCount ?? 0));
  const thisMonth = history?.thisMonth || null;
  const yours = thisMonth?.yoursCents ?? stats.revenueThisMonthCents ?? 0;
  const headerRight = `${priceLabel(club)} · ${paying} PAYING`;
  const statList = [
    [String(paying), phone ? 'PAYING' : 'PAYING MEMBERS', '#000'],
    [money(stats.revenueThisMonthCents ?? 0), 'THIS MONTH', GREEN],
    [money(yours), 'YOURS', '#000'],
    [String(lapsed), 'LAPSED', lapsed ? RED : '#000'],
  ];
  const months = history?.months || [];
  const r = phone ? 22 : 24;

  const header = (
    <section className="flex flex-wrap items-center" style={{ gap: '10px 20px', padding: phone ? '18px 16px' : '24px 28px', borderRadius: r, background: '#000', color: '#fff' }} data-testid="revenue-header">
      <span className="flex min-w-0 flex-col" style={{ gap: 6 }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.2em', color: CYAN }}>MEMBERSHIPS &amp; REVENUE</span>
        <h1 className="m-0" style={{ fontSize: phone ? 22 : 30, fontWeight: 700, lineHeight: 1.1, textTransform: 'none' }}>{club.name}</h1>
      </span>
      <span style={{ marginLeft: 'auto', padding: '8px 14px', borderRadius: 999, background: '#1F1F1F', fontSize: 11, fontWeight: 700, letterSpacing: '.12em' }} data-testid="header-right">{headerRight}</span>
    </section>
  );

  const statsStrip = (
    <div className="grid overflow-hidden" style={{ gridTemplateColumns: phone ? 'repeat(2,minmax(0,1fr))' : 'repeat(4,minmax(0,1fr))', gap: 4, borderRadius: r }} data-testid={phone ? 'stats-mobile' : 'stats'}>
      {statList.map(([n, k, c]) => (
        <span key={k} className="flex flex-col items-center bg-white text-center" style={{ gap: 6, padding: phone ? '16px 8px' : '22px 12px' }}>
          <span style={{ fontSize: phone ? 20 : 28, fontWeight: 800, color: c }}>{n}</span>
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.14em', color: MUTED }}>{k}</span>
        </span>
      ))}
    </div>
  );

  const thisMonthPanel = (
    <ClubPanel phone={phone} style={{ gap: 10 }} data-testid="this-month">
      <span style={{ ...panelTitle, paddingBottom: 4 }}>THIS MONTH</span>
      {[
        [plural(thisMonth?.memberships ?? thisMonth?.invoiceCount ?? paying, 'membership').toLowerCase(), money(thisMonth?.amountCents ?? stats.revenueThisMonthCents ?? 0)],
        ...(thisMonth ? [
          ['Portal service fee · members paid', money(thisMonth.prServiceFeeCents ?? 0)],
          ['GST collected · members paid', money(thisMonth.gstCents ?? 0)],
          ['Processing · members paid', money(thisMonth.stripeFeeCents ?? 0)],
        ] : []),
      ].map(([k, v]) => (
        <span key={k} className="flex justify-between" style={{ gap: 12, fontSize: 12 }}><span style={{ color: MUTED }}>{k}</span><span style={{ fontWeight: 700 }}>{v}</span></span>
      ))}
      <span className="flex justify-between" style={{ gap: 12, paddingTop: 10, borderTop: '2px solid #000', fontSize: 16, fontWeight: 800 }}>
        <span style={{ letterSpacing: '.06em' }}>YOURS</span>
        <span style={{ color: GREEN }} data-testid="yours">{money(yours)}</span>
      </span>
      <span style={{ padding: '12px 14px', borderRadius: 16, background: INSET, fontSize: 11, lineHeight: 1.6, color: '#374151' }}>
        Fees are charged to members on top of the price, so the full price is yours. Paid to your wallet the day each charge clears. GST is remitted by us; you never file it.
      </span>
    </ClubPanel>
  );

  const barsPanel = months.length > 0 ? (
    <ClubPanel phone={phone} style={{ gap: 12 }} data-testid="last-6-months">
      <span style={panelTitle}>LAST {months.length} MONTHS</span>
      <Bars months={months} />
    </ClubPanel>
  ) : (
    <ClubPanel phone={phone} data-testid="no-history">
      <span style={panelTitle}>LAST 6 MONTHS</span>
      <span className="text-center" style={{ padding: '20px 12px', borderRadius: 16, background: INSET, fontSize: 12, lineHeight: 1.6, color: MUTED }}><span style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '.16em', color: SUBTLE }}>NO HISTORY YET.</span>Monthly revenue shows up here once the first membership clears.</span>
    </ClubPanel>
  );

  const roster = (
    <ClubPanel phone={phone} style={{ gap: 14 }} data-testid="roster">
      <span className="flex flex-wrap items-center justify-between" style={{ gap: 10 }}>
        <span style={panelTitle}>MEMBERS · {members.length}</span>
        <PillButton variant="tertiary" size={34} fontSize={10} onClick={exportCsv} disabled={members.length === 0} style={{ padding: '0 16px', opacity: members.length ? 1 : 0.5 }}>EXPORT CSV</PillButton>
      </span>
      <span className="flex" style={{ gap: 6, overflowX: 'auto', scrollbarWidth: 'none' }} role="group" aria-label="Filter members">
        {FILTERS.map(([key, label]) => {
          const on = statusFilter === key;
          return (
            <button key={key || 'all'} type="button" aria-pressed={on} onClick={() => setStatusFilter(key)} className="flex-shrink-0 font-mono uppercase"
              style={{ height: 34, padding: '0 14px', border: '2px solid transparent', borderRadius: 999, background: on ? '#000' : CHIP, color: on ? '#fff' : '#000', fontSize: 10, fontWeight: 700, letterSpacing: '.12em', cursor: 'pointer' }}>
              {label}
            </button>
          );
        })}
      </span>
      {membersLoading && <div className="py-6 text-center"><LoadingSpinner size="sm" /></div>}
      {!membersLoading && members.length === 0 && <span className="text-center" style={{ padding: 28, fontSize: 12, color: MUTED }}>No members match the filter.</span>}
      {!membersLoading && members.length > 0 && !phone && (
        <span className="flex items-center" style={{ gap: 14, fontSize: 9, fontWeight: 700, letterSpacing: '.14em', color: SUBTLE }} data-testid="roster-columns">
          <span style={{ width: 40 }} /><span style={{ flex: '1 1 160px' }}>MEMBER</span><span style={{ width: 150 }}>STATUS</span><span style={{ width: 70, textAlign: 'right' }}>PAID</span><span style={{ minWidth: 150 }} />
        </span>
      )}
      {!membersLoading && (
        <div className="flex flex-col">
          {members.map((m, i) => {
            const pill = statusPill(m.paidStatus);
            const look = PILL_LOOK[pill.tone] || PILL_LOOK.ghost;
            const through = throughLabel(m);
            const canRefund = m.paidStatus === 'active' && m.paidPriceCents > 0;
            const canComp = m.paidStatus !== 'comp' && m.paidStatus !== 'active';
            const refundArmed = isArmed(`refund:${m.userId}`);
            const compArmed = isArmed(`comp:${m.userId}`);
            return (
              <div key={m.id || m.userId} className="flex flex-wrap items-center" style={{ gap: '10px 14px', padding: '12px 0', borderTop: i ? RULE : 'none' }} data-testid="member-row">
                <Face src={m.avatarUrl} name={m.displayName} tintKey={m.userId} size={40} />
                <span className="flex min-w-0 flex-col" style={{ flex: '1 1 160px', gap: 3 }}>
                  <span className="truncate" style={{ fontSize: 13, fontWeight: 700 }}>{m.displayName}</span>
                  <span className="truncate" style={{ fontSize: 11, color: MUTED }}>{m.handle ? `@${m.handle} · ` : ''}since {monthYearLabel(m.joinedAt)}</span>
                </span>
                <span className="flex flex-none flex-col items-start" style={{ gap: 4, width: phone ? 'auto' : 150 }}>
                  <TagPill bg={look.bg} fg={look.fg} ring={look.ring} size={9}>{pill.label}</TagPill>
                  {through && <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.12em', color: SUBTLE }}>{through}</span>}
                </span>
                <span className="flex-none text-right" style={{ width: 70, fontSize: 14, fontWeight: 800 }}>{m.paidPriceCents != null ? money(m.paidPriceCents) : '—'}</span>
                <span className="flex flex-none justify-end" style={{ gap: 6, minWidth: phone ? 0 : 150 }}>
                  {canRefund && (
                    <PillButton variant={refundArmed ? 'armed' : 'danger'} size={32} fontSize={9} tracking=".12em" onClick={() => handleRefund(m)} onBlur={refundArmed ? disarm : undefined} aria-label={refundArmed ? `Confirm refund ${m.displayName}` : `Refund ${m.displayName}`}>
                      {refundArmed ? `REFUND ${money(m.paidPriceCents || 0)}?` : 'REFUND'}
                    </PillButton>
                  )}
                  {canComp && (
                    <PillButton variant={compArmed ? 'primary' : 'tertiary'} size={32} fontSize={9} tracking=".12em" onClick={() => handleComp(m)} onBlur={compArmed ? disarm : undefined} aria-label={compArmed ? `Confirm comp ${m.displayName}` : `Comp ${m.displayName}`}>
                      {compArmed ? 'COMP · FREE FOR LIFE?' : 'COMP'}
                    </PillButton>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </ClubPanel>
  );

  const rail = (
    <div className="flex min-w-0 flex-col" style={{ gap: 12, position: phone ? 'static' : 'sticky', top: 88 }} data-testid="revenue-rail">
      {thisMonthPanel}
      {barsPanel}
    </div>
  );

  return frame(
    <>
      {header}
      {statsStrip}
      <div className="grid items-start" style={{ gridTemplateColumns: phone ? 'minmax(0,1fr)' : 'minmax(0,1fr) 340px', gap: phone ? 10 : 16 }}>
        {phone ? (<>{rail}{roster}</>) : (<>{roster}{rail}</>)}
      </div>
    </>,
  );
}
